import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload } from 'lucide-react';
import {
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_VIDEO_MIME_TYPES,
  MAX_IMAGE_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_BYTES,
  isImageMediaKind,
  type MediaAssetDto,
  type MediaAssetKind,
} from '@gc/shared';

import { uploadMedia } from './upload';

/**
 * Form primitives shared by the settings screens.
 *
 * They exist so the §6.1 page — which is a wall of ~30 fields across two
 * columns — reads as its field list rather than as 400 lines of identical
 * Tailwind, and so every screen in the area stays visually consistent.
 */

export const inputClass =
  'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

export const pillClass =
  'rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60';

export const ghostPillClass =
  'rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover';

/** A labelled row. `hint` carries the spec's parenthetical wording. */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-content-secondary">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-content-muted">{hint}</span> : null}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
    </label>
  );
}

/**
 * `Field`'s markup for a setting made of MORE THAN ONE control.
 *
 * A `<label>` binds to its first labelable descendant, so wrapping two controls
 * in one would hand every click on the caption to whichever happens to be first
 * — on the hero-video field that is the hidden file input the moment the video
 * library is empty, i.e. clicking the caption would pop a file dialog. A div
 * with a caption is the honest markup for a group; each control inside carries
 * its own label.
 */
export function FieldGroup({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="block">
      <span className="mb-1 block text-sm text-content-secondary">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-content-muted">{hint}</span> : null}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
    </div>
  );
}

/** A boolean setting. The §6.1 page is mostly these. */
export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-2 rounded-control border border-subtle bg-input px-3 py-2">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 accent-[var(--brand-primary)]"
      />
      <span>
        <span className="block text-sm text-content">{label}</span>
        {hint ? <span className="block text-xs text-content-muted">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * Groups a handful of related settings under a heading.
 *
 * `id` is the ANCHOR the settings search jumps to (`/settings/portal#set-hotkeys`),
 * so it belongs on the element the browser will scroll to, and it has to stay
 * stable: renaming one turns a search result into a link that lands on the top
 * of the page with no sign anything was missed. `scroll-mt-4` keeps the heading
 * clear of the scroll container's edge when it is jumped to.
 */
export function Section({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-4 rounded-card border border-subtle bg-raised p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-content-secondary">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/**
 * A settings page's heading: its name, and one line saying what it decides.
 *
 * The sentence is not decoration. Split across four pages, the only thing that
 * tells the owner whether the printer settings are on THIS page is this line —
 * the old single page let him scroll until he found the field.
 */
export function PageHeading({ title, description }: { title: string; description: string }) {
  return (
    <header className="mb-4">
      <h1 className="text-xl font-semibold text-content">{title}</h1>
      <p className="mt-1 text-sm text-content-secondary">{description}</p>
    </header>
  );
}

/**
 * The Confirm row every General-Settings form ends with.
 *
 * STICKY, because each of the four pages still scrolls past a screen on a
 * cashier monitor and a Confirm button that has scrolled out of sight is how
 * an edit gets abandoned half-made. `-mx-6 px-6` lets the bar bleed to the
 * page's edges while the form keeps its padding — `-mx-4 px-4` on a phone,
 * where SettingsLayout trims every page's gutter to 16px; bleeding by 24px
 * there would push the bar past the screen edge and scroll the page sideways.
 *
 * It needs no safe-area padding of its own: `body` already pads clear of the
 * home indicator, so the bottom of the scroller is above it.
 */
export function SaveBar({
  saving,
  saved,
  error,
}: {
  saving: boolean;
  saved: boolean;
  /** The page-level failure (`(root)`); per-field messages render at the field. */
  error?: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      {error ? (
        <p role="alert" className="mt-4 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="sticky bottom-0 -mx-6 mt-5 flex flex-wrap items-center gap-3 border-t border-subtle bg-ground/95 px-6 py-3 backdrop-blur compact:-mx-4 compact:px-4">
        <button type="submit" disabled={saving} className={pillClass}>
          {t('common.confirm')}
        </button>
        {saved ? <span className="text-sm text-success">{t('settings.general.saved')}</span> : null}
      </div>
    </>
  );
}

/**
 * The `accept` attribute for a media kind, BUILT FROM the shared allow-list.
 *
 * Never hand-type this list. The literal that used to live in the file picker
 * was a second source of truth for the same set, and drifting from it is how
 * `DEVICE_IMAGE` shipped broken once already (see
 * `packages/shared/src/media-kinds.test.ts`, which exists because of it).
 */
export function mediaAccept(kind: MediaAssetKind): string {
  return (isImageMediaKind(kind) ? ALLOWED_IMAGE_MIME_TYPES : ALLOWED_VIDEO_MIME_TYPES).join(',');
}

/** The server's ceiling for this kind — the same number, checked earlier. */
export function maxMediaBytes(kind: MediaAssetKind): number {
  return isImageMediaKind(kind) ? MAX_IMAGE_UPLOAD_BYTES : MAX_VIDEO_UPLOAD_BYTES;
}

/** Whole megabytes, for the "that file is too big" message. */
function megabytes(bytes: number): number {
  return Math.round(bytes / (1024 * 1024));
}

/**
 * Media upload with preview and delete (SPEC §6.1 logo, §6.4 avatar, §6.6
 * covers and trailers, §7 idle video, and the Device List's `imageUrl`).
 *
 * Uploads immediately and hands back the asset id: the file has to exist on the
 * server before the record referencing it is saved, otherwise a failed upload
 * would be discovered only when the form is submitted.
 *
 * Everything that differs between a picture and a video — the accepted types,
 * the size ceiling, the preview element — comes off `isImageMediaKind(kind)`,
 * so a caller only ever names the kind.
 */
export function MediaUpload({
  label,
  kind,
  currentUrl,
  previewClassName,
  onUploaded,
  onCleared,
}: {
  label: string;
  kind: MediaAssetKind;
  currentUrl: string | null;
  /**
   * Size of the preview box. The thumbnail default is right for a row of
   * covers and avatars; the hero-video field overrides it, because a video the
   * owner is choosing as the venue's front page has to be big enough to judge.
   */
  previewClassName?: string;
  /**
   * The whole asset comes third so a caller that keeps its own library list
   * (the hero-video picker) can show the new file by name without refetching.
   */
  onUploaded: (assetId: string, url: string, asset: MediaAssetDto) => void;
  onCleared: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isImage = isImageMediaKind(kind);
  const sizeLimit = maxMediaBytes(kind);

  async function handleFile(file: File | undefined): Promise<void> {
    if (!file) return;

    // BEFORE the POST, always. A 512MB video refused after five minutes of
    // upload is the worst possible version of this message, and on a venue's
    // uplink that is exactly how long it takes to find out.
    if (file.size > sizeLimit) {
      setError(t('settings.media.tooLarge', { limit: megabytes(sizeLimit) }));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const asset = await uploadMedia(file, kind);
      onUploaded(asset.id, asset.url, asset);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('settings.media.uploadFailed'));
    } finally {
      setBusy(false);
      // Clearing lets the same file be re-picked after a failure.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div>
      <span className="mb-1 block text-sm text-content-secondary">{label}</span>
      <div className="flex flex-wrap items-center gap-3">
        {isImage ? (
          <div
            data-media-preview="image"
            className={`grid shrink-0 place-items-center overflow-hidden rounded-control border border-subtle bg-input ${previewClassName ?? 'h-16 w-16'}`}
          >
            {currentUrl ? (
              <img src={currentUrl} alt="" className="h-full w-full object-contain" />
            ) : (
              <Upload className="h-5 w-5 text-content-muted" aria-hidden />
            )}
          </div>
        ) : (
          // 16:9, because that is the shape the thing plays back in — a video
          // previewed in a square box is a video nobody can judge the framing of.
          <div
            data-media-preview="video"
            className={`grid shrink-0 place-items-center overflow-hidden rounded-control border border-subtle bg-black ${previewClassName ?? 'aspect-video w-28'}`}
          >
            {currentUrl ? (
              <video
                src={currentUrl}
                muted
                controls
                playsInline
                className="h-full w-full object-contain"
              />
            ) : (
              <Upload className="h-5 w-5 text-content-muted" aria-hidden />
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={mediaAccept(kind)}
          onChange={(event) => void handleFile(event.target.files?.[0])}
          className="hidden"
        />

        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className={ghostPillClass}
        >
          {busy ? t('settings.media.uploading') : t('settings.media.upload')}
        </button>

        {currentUrl ? (
          <button
            type="button"
            onClick={onCleared}
            title={t('common.delete')}
            aria-label={t('common.delete')}
            className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  );
}

/**
 * The original name, kept so the existing image call sites read as what they
 * are. Identical behaviour — `MediaUpload` already branches on the kind.
 */
export const ImageUpload = MediaUpload;

/**
 * Explains why a screen or control is unavailable.
 *
 * SPEC/AGENTS rule: a control that does nothing is worse than one visibly
 * disabled with a reason.
 */
export function UnavailableNotice({ title, reason }: { title: string; reason: string }) {
  return (
    <div className="rounded-card border border-subtle bg-raised p-4">
      <h2 className="text-sm font-semibold text-content">{title}</h2>
      <p className="mt-1 text-sm text-content-muted">{reason}</p>
    </div>
  );
}
